import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Plus, Trash2, GripVertical, Save, FileText, Eye, ChevronUp, ChevronDown } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { useAuth } from '@/lib/AuthContext';
import { isAdminOrOwner } from '@/lib/roles';
import AppHeader from '@/components/drill/AppHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/components/ui/use-toast';

const ROLE_OPTIONS = [
  { value: 'rig', label: 'Rig' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'admin', label: 'Admin' },
  { value: 'owner', label: 'Owner' },
];

const FIELD_OPTIONS = [
  ['hole_id', 'Hole ID'],
  ['rig_id', 'Rig'],
  ['method', 'Drilling Method'],
  ['status', 'Hole Status'],
  ['current_depth', 'Current Depth'],
  ['run_count', 'Run Count'],
  ['today_drilled', "Today's Metres"],
  ['last_run_from', 'Last Run From'],
  ['last_run_to', 'Last Run To'],
  ['date', 'Current Date'],
];

function blankBlock(type = 'text') {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    type,
    text: type === 'heading' ? 'Section heading' : type === 'field' ? '' : 'Enter text…',
    field: 'hole_id',
  };
}

export default function DocumentBuilder() {
  const { effectiveRole } = useAuth();
  const allowed = isAdminOrOwner(effectiveRole);
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [permissions, setPermissions] = useState(['rig', 'supervisor', 'admin', 'owner']);
  const [blocks, setBlocks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  const load = async () => {
    if (!allowed) return;
    try {
      setTemplates(await base44.entities.DocumentTemplate.list('-updated_date'));
    } catch (e) {
      toast({ title: 'Unable to load documents', description: e?.message || 'Please try again.', variant: 'destructive' });
    }
  };

  useEffect(() => { void load(); }, [allowed]);

  const selected = useMemo(() => templates.find((t) => t.id === selectedId), [templates, selectedId]);

  const startNew = () => {
    setSelectedId(null);
    setTitle('');
    setDescription('');
    setPermissions(['rig', 'supervisor', 'admin', 'owner']);
    setBlocks([blankBlock('heading'), blankBlock('field'), blankBlock('text')]);
  };

  const openTemplate = (template) => {
    setSelectedId(template.id);
    setTitle(template.title || '');
    setDescription(template.description || '');
    setPermissions(Array.isArray(template.permissions) && template.permissions.length ? template.permissions : ['rig', 'supervisor', 'admin', 'owner']);
    setBlocks(Array.isArray(template.blocks) ? template.blocks : []);
  };

  const updateBlock = (id, patch) => setBlocks((items) => items.map((b) => b.id === id ? { ...b, ...patch } : b));
  const removeBlock = (id) => setBlocks((items) => items.filter((b) => b.id !== id));
  const moveBlock = (index, direction) => setBlocks((items) => {
    const next = [...items];
    const target = index + direction;
    if (target < 0 || target >= next.length) return items;
    [next[index], next[target]] = [next[target], next[index]];
    return next;
  });

  const togglePermission = (role) => setPermissions((prev) => prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]);

  const save = async () => {
    if (!title.trim()) return;
    if (!permissions.length) {
      toast({ title: 'Select at least one permission', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        permissions,
        blocks,
        version: selected?.version ? Number(selected.version) + 1 : 1,
        status: 'active',
      };
      const saved = selectedId
        ? await base44.entities.DocumentTemplate.update(selectedId, payload)
        : await base44.entities.DocumentTemplate.create(payload);
      setSelectedId(saved.id);
      await load();
      toast({ title: 'Document saved' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.message || 'Please try again.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!selectedId || !window.confirm('Delete this document template?')) return;
    await base44.entities.DocumentTemplate.delete(selectedId);
    startNew();
    await load();
    toast({ title: 'Document deleted' });
  };

  if (!allowed) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      <AppHeader title="Document Builder" subtitle="Create organisation documents with role permissions" backTo="/" />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
          <section className="rounded-2xl border bg-white p-4 dark:bg-slate-900 dark:border-slate-800">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold">Documents</h2>
              <Button size="sm" className="rounded-xl gap-1.5" onClick={startNew}><Plus className="w-4 h-4" /> New</Button>
            </div>
            <div className="space-y-2">
              {templates.map((template) => (
                <button key={template.id} onClick={() => openTemplate(template)} className={`w-full text-left rounded-xl border p-3 ${selectedId === template.id ? 'border-slate-900 bg-slate-50 dark:border-slate-300 dark:bg-slate-800' : 'border-slate-200 dark:border-slate-800'}`}>
                  <div className="font-medium truncate">{template.title}</div>
                  <div className="text-xs text-slate-500 mt-1">v{template.version || 1} · {(template.permissions || []).join(', ')}</div>
                </button>
              ))}
              {!templates.length && <p className="text-sm text-slate-400 py-5 text-center">No documents yet.</p>}
            </div>
          </section>

          <section className="rounded-2xl border bg-white p-5 dark:bg-slate-900 dark:border-slate-800">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div><h2 className="text-lg font-semibold">{selectedId ? 'Edit document' : 'New document'}</h2><p className="text-sm text-slate-500">Build the document from reusable blocks and DLP fields.</p></div>
              <div className="flex gap-2">
                {selectedId && <Button variant="outline" className="rounded-xl gap-2" onClick={remove}><Trash2 className="w-4 h-4" /> Delete</Button>}
                <Button className="rounded-xl gap-2" onClick={save} disabled={saving}><Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save Document'}</Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
              <div><Label>Document name *</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. End of Shift Report" className="rounded-xl mt-1" /></div>
              <div><Label>Description</Label><Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What this document is used for" className="rounded-xl mt-1" /></div>
            </div>

            <div className="mt-5 rounded-2xl border p-4 dark:border-slate-800">
              <Label>Who can use this document?</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                {ROLE_OPTIONS.map((role) => <label key={role.value} className="flex items-center gap-2 text-sm"><Checkbox checked={permissions.includes(role.value)} onCheckedChange={() => togglePermission(role.value)} />{role.label}</label>)}
              </div>
            </div>

            <div className="mt-5 flex items-center justify-between"><div><h3 className="font-semibold">Document blocks</h3><p className="text-xs text-slate-500">Blocks are rendered in order.</p></div><div className="flex gap-2"><Button variant="outline" className="rounded-xl" onClick={() => setBlocks((b) => [...b, blankBlock('heading')])}>Heading</Button><Button variant="outline" className="rounded-xl" onClick={() => setBlocks((b) => [...b, blankBlock('field')])}>DLP Field</Button><Button variant="outline" className="rounded-xl" onClick={() => setBlocks((b) => [...b, blankBlock('text')])}>Text</Button></div></div>

            <div className="space-y-3 mt-4">
              {blocks.map((block, index) => (
                <div key={block.id} draggable onDragStart={() => setDragIndex(index)} onDragOver={(e) => e.preventDefault()} onDrop={() => { if (dragIndex == null || dragIndex === index) return; setBlocks((items) => { const next=[...items]; const [moved]=next.splice(dragIndex,1); next.splice(index,0,moved); return next; }); setDragIndex(null); }} onDragEnd={() => setDragIndex(null)} className="rounded-2xl border p-4 dark:border-slate-800">
                  <div className="flex items-center gap-2 mb-3"><GripVertical className="w-4 h-4 text-slate-400" /><span className="text-xs font-semibold uppercase text-slate-400">{block.type}</span><div className="ml-auto flex gap-1"><Button variant="ghost" size="icon" onClick={() => moveBlock(index, -1)} disabled={index === 0}><ChevronUp className="w-4 h-4" /></Button><Button variant="ghost" size="icon" onClick={() => moveBlock(index, 1)} disabled={index === blocks.length - 1}><ChevronDown className="w-4 h-4" /></Button><Button variant="ghost" size="icon" onClick={() => removeBlock(block.id)}><Trash2 className="w-4 h-4" /></Button></div></div>
                  {block.type === 'field' ? (
                    <select value={block.field} onChange={(e) => updateBlock(block.id, { field: e.target.value })} className="w-full h-11 rounded-xl border bg-white px-3 text-sm dark:bg-slate-900 dark:border-slate-700">
                      {FIELD_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  ) : block.type === 'heading' ? (
                    <Input value={block.text} onChange={(e) => updateBlock(block.id, { text: e.target.value })} className="rounded-xl font-semibold" />
                  ) : (
                    <Textarea value={block.text} onChange={(e) => updateBlock(block.id, { text: e.target.value })} className="rounded-xl" />
                  )}
                </div>
              ))}
              {!blocks.length && <div className="rounded-2xl border border-dashed p-8 text-center text-sm text-slate-400">Add a block to start building the document.</div>}
            </div>

            <div className="mt-6 rounded-2xl border bg-slate-50 p-4 dark:bg-slate-950 dark:border-slate-800">
              <div className="flex items-center gap-2 font-semibold"><Eye className="w-4 h-4" /> Preview</div>
              <div className="mt-3 bg-white rounded-xl p-5 min-h-32 dark:bg-slate-900">
                <h1 className="text-xl font-bold">{title || 'Untitled document'}</h1>
                {description && <p className="text-sm text-slate-500 mt-1">{description}</p>}
                <div className="mt-4 space-y-3">
                  {blocks.map((block) => block.type === 'field' ? <div key={block.id} className="text-sm"><span className="text-slate-500">{FIELD_OPTIONS.find(([v]) => v === block.field)?.[1] || block.field}:</span> <span className="font-medium">{{hole_id:'Hole ID',rig_id:'Rig',method:'Method',status:'Active',current_depth:'0.00 m',run_count:'0',today_drilled:'0.00 m',last_run_from:'0.00 m',last_run_to:'0.00 m',date:new Date().toLocaleDateString()}[block.field]}</span></div> : block.type === 'heading' ? <h3 key={block.id} className="font-semibold text-base pt-2">{block.text}</h3> : <p key={block.id} className="text-sm whitespace-pre-wrap">{block.text}</p>)}
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
